import type { Money } from '@/services/types'

/** Formateo solo en la capa de UI con Intl (inventario D25). `lang` es el idioma de i18next. */
const LOCALE: Record<string, string> = { es: 'es-ES', en: 'en-GB', it: 'it-IT', fr: 'fr-FR' }
export const locale = (lang: string) => LOCALE[lang] ?? lang

export function formatMoney(m: Money, lang: string): string {
  return new Intl.NumberFormat(locale(lang), { style: 'currency', currency: m.currency }).format(m.amount / 100)
}

/** "300 €" sin decimales cuando son cero (filas de pasos: "Fianza de 300 €"). */
export function formatMoneyShort(m: Money, lang: string): string {
  const whole = m.amount % 100 === 0
  return new Intl.NumberFormat(locale(lang), { style: 'currency', currency: m.currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(m.amount / 100)
}

const dt = (lang: string, options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale(lang), options)

/** "sáb 3" */
export const weekdayDay = (iso: string, lang: string) => dt(lang, { weekday: 'short', day: 'numeric' }).format(new Date(iso)).replace(',', '')
/** "sáb 3 oct" */
export const weekdayDayMonth = (iso: string, lang: string) => dt(lang, { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(iso)).replace(',', '')
/** "sábado 3" */
export const weekdayLongDay = (iso: string, lang: string) => dt(lang, { weekday: 'long', day: 'numeric' }).format(new Date(iso)).replace(',', '')
/** "martes 6" */
export const weekdayLong = (iso: string, lang: string) => dt(lang, { weekday: 'long' }).format(new Date(iso))
/** "3 oct" */
export const dayMonth = (iso: string, lang: string) => dt(lang, { day: 'numeric', month: 'short' }).format(new Date(iso))
/** "6 oct, 11:00" */
export const dayMonthTime = (iso: string, lang: string) => `${dayMonth(iso, lang)}, ${time(iso, lang)}`
/** "3 de octubre" */
export const dayMonthLong = (iso: string, lang: string) => dt(lang, { day: 'numeric', month: 'long' }).format(new Date(iso))
/** "16:00" */
export const time = (iso: string, lang: string) => dt(lang, { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso))
/** "3 – 6 oct" (mismo mes) o "30 sep – 2 oct" */
export function dateRange(fromIso: string, toIso: string, lang: string): string {
  const from = new Date(fromIso)
  const to = new Date(toIso)
  const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()
  const left = sameMonth ? dt(lang, { day: 'numeric' }).format(from) : dayMonth(fromIso, lang)
  return `${left} – ${dayMonth(toIso, lang)}`
}

/** Días naturales entre hoy y una fecha (cuenta atrás "17 días"). */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const target = new Date(iso)
  target.setHours(0, 0, 0, 0)
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - today.getTime()) / 86400000)
}

/** Noche en curso (1..nights): la noche N empieza el día checkIn + (N − 1). */
export function currentNight(checkInIso: string, now: Date = new Date()): number {
  return Math.max(1, -daysUntil(checkInIso, now) + 1)
}

/** "registro, fianza y firma" */
export function listJoin(items: string[], lang: string): string {
  if (typeof Intl.ListFormat === 'function') return new Intl.ListFormat(locale(lang), { style: 'long', type: 'conjunction' }).format(items)
  return items.join(', ')
}

export const telHref = (phone: string) => `tel:${phone.replace(/[\s()-]/g, '')}`
export const mapsHref = (lat: number | null, lng: number | null, address: string) => (lat !== null && lng !== null ? `https://maps.google.es/?q=${lat},${lng}` : `https://maps.google.es/?q=${encodeURIComponent(address)}`)
/** Vista embebida, sin clave de Maps: el mismo destino de coordenadas/dirección que el enlace de navegación. */
export const mapsEmbedSrc = (lat: number | null, lng: number | null, address: string) => {
  const query = lat !== null && lng !== null ? `${lat},${lng}` : address
  return `https://www.google.com/maps?output=embed&z=16&q=${encodeURIComponent(query)}`
}
